import { useCallback, useEffect, useState } from "react";

// Chrome's BeforeInstallPromptEvent is not in the DOM lib typings.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

// `beforeinstallprompt` can fire before any component mounts, so the listener lives at module
// scope (SSR-guarded) and hooks subscribe to it.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export const detectIos = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

export const detectStandalone = (): boolean => {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
};

/**
 * PWA install entry point (MOD-12). `canPrompt` is true only after Chrome captured
 * `beforeinstallprompt`; iOS never fires it, so callers show the Share-sheet hint when `isIos`.
 */
export function useInstallPrompt() {
  const [canPrompt, setCanPrompt] = useState<boolean>(() => deferredPrompt !== null);
  const [isIos] = useState<boolean>(detectIos);
  const [isStandalone, setIsStandalone] = useState<boolean>(detectStandalone);

  useEffect(() => {
    const sync = () => {
      setCanPrompt(deferredPrompt !== null);
      setIsStandalone(detectStandalone());
    };
    listeners.add(sync);
    sync();
    return () => {
      listeners.delete(sync);
    };
  }, []);

  const prompt = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    const event = deferredPrompt;
    if (!event) return "unavailable";
    // A captured event can be used once.
    deferredPrompt = null;
    notify();
    try {
      await event.prompt();
      const choice = await event.userChoice;
      return choice.outcome;
    } catch {
      return "dismissed";
    }
  }, []);

  return { canPrompt, prompt, isIos, isStandalone };
}
